import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Link2, Mail, Users } from "lucide-react";
import { Button, Modal } from "../../../components";
import {
  CUSTOMER_INVITE_URL,
  CUSTOMER_POSSIBLE_MATCHES_URL,
  CUSTOMER_MERGE_URL,
} from "../../../features/UrlHelper";
import "./PozvankaModal.scss";

/**
 * Pozvanie zákazníka do webového objednávania (R25) a spárovanie dvoch účtov.
 *
 * PREČO OBE V JEDNOM MODÁLI:
 *   Sú to dve odpovede na tú istú situáciu — „tento človek si má vedieť
 *   objednať cez web“. Keď taký účet už existuje (zaregistroval sa sám),
 *   pozvánka by založila druhý a prevádzka by mala jedného človeka dvakrát,
 *   každého s polovicou histórie. Preto sa možní dvojníci podľa telefónu
 *   ukazujú hneď nad formulárom pozvánky, nie až keď sa problém stane.
 *
 * Pozvánka ide ako podpísaný odkaz na e-mail; heslo si zákazník nastaví sám.
 * Server odmietne pozvať niekoho, kto už heslo má.
 */
function PozvankaModal({ isOpen, onClose, customer, onHotovo }) {
  const [email, setEmail] = useState("");
  const [matches, setMatches] = useState([]);
  const [pracuje, setPracuje] = useState(false);
  const [chyba, setChyba] = useState("");
  const [hlaska, setHlaska] = useState("");

  useEffect(() => {
    if (!isOpen || !customer) return;
    setEmail(customer.email || "");
    setChyba("");
    setHlaska("");
    setMatches([]);

    axios
      .get(CUSTOMER_POSSIBLE_MATCHES_URL(customer.id))
      .then(({ data }) => setMatches(data?.matches || []))
      // Dvojníci sú pomôcka, nie podmienka — bez nich sa pozvánka pošle tiež.
      .catch(() => setMatches([]));
  }, [isOpen, customer]);

  if (!customer) return null;

  const posliPozvanku = async (e) => {
    e.preventDefault();
    setPracuje(true);
    setChyba("");
    setHlaska("");
    try {
      await axios.post(CUSTOMER_INVITE_URL(customer.id), { email: email.trim().toLowerCase() });
      setHlaska(`Pozvánku sme poslali na ${email.trim().toLowerCase()}. Platí 7 dní.`);
      onHotovo?.();
    } catch (err) {
      setChyba(err.response?.data?.message || "Pozvánku sa nepodarilo odoslať.");
    } finally {
      setPracuje(false);
    }
  };

  const sparuj = async (sourceId) => {
    setPracuje(true);
    setChyba("");
    setHlaska("");
    try {
      const { data } = await axios.post(CUSTOMER_MERGE_URL(customer.id), { sourceId });
      setHlaska(data?.message || "Účty sú spárované.");
      setMatches([]);
      onHotovo?.();
    } catch (err) {
      setChyba(err.response?.data?.message || "Účty sa nepodarilo spárovať.");
    } finally {
      setPracuje(false);
    }
  };

  const meno = [customer.name, customer.surname].filter(Boolean).join(" ");

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Webový účet — ${meno}`}>
      {matches.length > 0 && (
        <div className="pozvanka__matches">
          <p className="pozvanka__matches-title">
            <Users size={16} aria-hidden="true" />
            Na rovnakom telefónnom čísle máte aj tieto kontá
          </p>
          <p className="pozvanka__hint">
            Ak je to ten istý človek, spárujte ich — objednávky, saldo aj adresy sa zlúčia
            pod toto konto. Nová pozvánka by z neho spravila dvoch zákazníkov.
          </p>
          <ul className="pozvanka__list">
            {matches.map((m) => (
              <li key={m.id} className="pozvanka__item">
                <div className="pozvanka__item-info">
                  <strong>{[m.name, m.surname].filter(Boolean).join(" ") || "—"}</strong>
                  <span className="pozvanka__item-detail">
                    {m.email || "bez e-mailu"} · {m.pocet_objednavok} obj.
                    {m.ma_webovy_ucet ? " · má webový účet" : ""}
                  </span>
                </div>
                <Button variant="outline" onClick={() => sparuj(m.id)} disabled={pracuje}>
                  <Link2 size={15} aria-hidden="true" /> Spárovať
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <form className="pozvanka__form" onSubmit={posliPozvanku}>
        <label className="pozvanka__label" htmlFor="pozvanka-email">
          E-mail pre pozvánku
        </label>
        <input
          id="pozvanka-email"
          type="email"
          className="pozvanka__input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="meno@example.sk"
          disabled={pracuje}
        />
        <p className="pozvanka__hint">
          Pošleme naň odkaz, cez ktorý si zákazník nastaví heslo. Odkaz platí 7 dní.
          Jeho doterajšie objednávky a saldo zostávajú.
        </p>

        {chyba && <p className="pozvanka__error" role="alert">{chyba}</p>}
        {hlaska && (
          <p className="pozvanka__success">
            <CheckCircle2 size={15} aria-hidden="true" /> {hlaska}
          </p>
        )}

        <div className="pozvanka__actions">
          <Button variant="ghost" type="button" onClick={onClose} disabled={pracuje}>
            Zavrieť
          </Button>
          <Button variant="primary" type="submit" disabled={pracuje || !email.trim()}>
            <Mail size={15} aria-hidden="true" />
            {pracuje ? "Posielam..." : "Poslať pozvánku"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default PozvankaModal;
